import React, { useCallback, useEffect, useState } from "react";
import { Check, FileVideo, Loader2, Pencil, Play, Search, Square, Trash2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/use-toast";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { procedureRecordingsAPI } from "../services/api";
import ProcedureRecordingEditDialog from "./ProcedureRecordingEditDialog";
import ProcedureVideoPlayer from "./ProcedureVideoPlayer";

const formatDuration = (seconds = 0) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}min`;
  return `${minutes}:${String(secs).padStart(2, "0")} min`;
};

const formatSize = (bytes = 0) => {
  if (!bytes) return "";
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
};

const makeVideoThumbnail = (videoBlob) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const sourceUrl = URL.createObjectURL(videoBlob);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      video.pause();
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(sourceUrl);
      resolve(value);
    };
    const capture = () => {
      if (!video.videoWidth || !video.videoHeight) {
        finish(null);
        return;
      }
      const maxWidth = 480;
      const scale = Math.min(1, maxWidth / video.videoWidth);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((imageBlob) => finish(imageBlob ? URL.createObjectURL(imageBlob) : null), "image/webp", 0.8);
    };

    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;
    video.addEventListener("error", () => reject(new Error("Vídeo não pôde gerar miniatura")), { once: true });
    video.addEventListener("loadeddata", () => {
      if (video.duration && video.duration > 0.15) {
        video.addEventListener("seeked", capture, { once: true });
        video.currentTime = Math.min(0.15, Math.max(0, video.duration / 2));
      } else {
        capture();
      }
    }, { once: true });
    video.src = sourceUrl;
  });

function RecordingThumbnail({ recording }) {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);

  useEffect(() => {
    let disposed = false;
    let objectUrl = null;

    const loadThumbnail = async () => {
      try {
        if (recording.has_thumbnail) {
          const response = await procedureRecordingsAPI.getThumbnail(recording.id);
          objectUrl = URL.createObjectURL(response.data);
        } else {
          const response = await procedureRecordingsAPI.getVideo(recording.id);
          objectUrl = await makeVideoThumbnail(response.data);
        }
        if (!disposed) setThumbnailUrl(objectUrl);
      } catch (_) {
        if (!disposed) setThumbnailUrl(null);
      }
    };

    setThumbnailUrl(null);
    loadThumbnail();
    return () => {
      disposed = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [recording.has_thumbnail, recording.id]);

  if (thumbnailUrl) {
    return <img src={thumbnailUrl} alt="Miniatura do procedimento" className="h-full w-full object-cover" />;
  }
  return <div className="h-full w-full bg-gradient-to-br from-violet-700 to-indigo-900 flex items-center justify-center"><FileVideo className="w-9 h-9 text-white/80" /></div>;
}

export default function ProcedureRecordingsList({
  ticketId,
  refreshKey = 0,
  onCountChange,
  compact = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [recordings, setRecordings] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [editingRecording, setEditingRecording] = useState(null);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadRecordings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await procedureRecordingsAPI.list({
        ticket_id: ticketId || undefined,
        search: search.trim() || undefined,
        limit: selectable ? 60 : compact ? 20 : 100,
      });
      setRecordings(response.data || []);
      onCountChange?.(response.data?.length || 0);
    } catch (error) {
      console.error("Erro ao carregar procedimentos", error);
      setRecordings([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [compact, onCountChange, search, selectable, ticketId]);

  useEffect(() => {
    const debounce = window.setTimeout(loadRecordings, search ? 250 : 0);
    return () => window.clearTimeout(debounce);
  }, [loadRecordings, refreshKey, search]);

  const deleteRecording = async (recording) => {
    if (!recording || deletingId) return;
    setDeletingId(recording.id);
    try {
      await procedureRecordingsAPI.remove(recording.id);
      if (selectedRecording?.id === recording.id) setSelectedRecording(null);
      onSelectionChange?.(selectedIds.filter((id) => id !== recording.id));
      setRecordingToDelete(null);
      toast({ title: "Procedimento excluído", description: "O vídeo foi removido da biblioteca com sucesso." });
      await loadRecordings();
    } catch (error) {
      toast({
        title: "Erro ao excluir procedimento",
        description: error?.response?.data?.detail || "Não foi possível excluir o procedimento. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const toggleRecording = (recordingId) => {
    const nextIds = selectedIds.includes(recordingId)
      ? selectedIds.filter((id) => id !== recordingId)
      : [...selectedIds, recordingId];
    onSelectionChange?.(nextIds);
  };

  const showSearch = !compact || selectable;

  return (
    <>
      {showSearch && (
        <div className="relative mb-4 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por título, descrição, cliente ou protocolo..." className="pl-9 bg-white" />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : recordings.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-white px-5 py-10 text-center">
          <FileVideo className="w-9 h-9 mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-gray-700">Nenhum procedimento encontrado</p>
          <p className="text-sm text-gray-500 mt-1">As gravações concluídas aparecerão aqui.</p>
        </div>
      ) : (
        <div className={compact ? "space-y-2" : "grid gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
          {recordings.map((recording) => {
            const canDelete = user?.role === "admin" || user?.id === recording.created_by_id;
            const canEdit = user?.role === "admin" || user?.id === recording.created_by_id;
            const isSelected = selectedIds.includes(recording.id);
            return (
              <article
                key={recording.id}
                className={`min-w-0 overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-md transition-all ${compact ? `flex min-h-[96px] ${selectable ? "flex-col sm:flex-row" : ""}` : ""} ${isSelected ? "ring-2 ring-violet-500 border-violet-300" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedRecording(recording)}
                  className={`group relative block shrink-0 bg-gray-900 text-left ${compact ? selectable ? "m-3 mb-0 aspect-video w-[calc(100%-1.5rem)] overflow-hidden rounded-lg sm:mb-3 sm:h-24 sm:w-40 sm:aspect-auto" : "m-2 h-20 w-36 rounded-lg overflow-hidden" : "aspect-video w-full"}`}
                  title={`Reproduzir ${recording.title}`}
                >
                  <RecordingThumbnail recording={recording} />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                    <span className="rounded-full bg-white/90 p-2 text-violet-700 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all"><Play className="w-4 h-4 fill-current" /></span>
                  </span>
                  <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white">{formatDuration(recording.duration_seconds)}</span>
                </button>
                <div className={`min-w-0 flex-1 ${compact ? selectable ? "p-3 sm:pl-0" : "p-3 pl-1" : "p-4"}`}>
                  <button type="button" onClick={() => setSelectedRecording(recording)} className="block text-left w-full">
                    <h3 className={`font-semibold text-gray-900 hover:text-violet-700 ${selectable ? "[overflow-wrap:anywhere]" : compact ? "truncate" : "line-clamp-2"}`}>{recording.title}</h3>
                  </button>
                  <p className={`mt-1 text-sm text-gray-500 ${selectable ? "[overflow-wrap:anywhere]" : "truncate"}`}>{recording.client_name} · {recording.protocol}</p>
                  {recording.description && <p className={`mt-1 text-sm text-gray-600 ${selectable ? "line-clamp-3 whitespace-pre-line [overflow-wrap:anywhere]" : compact ? "truncate" : "line-clamp-2"}`}>{recording.description}</p>}
                  {!selectable && recording.available_in_bot && <span className="mt-2 inline-block rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">Disponível no bot</span>}
                  <div className={`flex items-center justify-between gap-2 text-xs text-gray-500 ${selectable ? "flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center" : ""} ${compact ? "mt-2" : "mt-3"}`}>
                    <span className={selectable ? "min-w-0 break-words" : "truncate"}>{new Date(recording.completed_at || recording.created_at).toLocaleDateString("pt-BR")}{recording.file_size ? ` · ${formatSize(recording.file_size)}` : ""}</span>
                    <span className={`flex items-center gap-1 shrink-0 ${selectable ? "sm:ml-auto" : ""}`}>
                      {selectable && (
                        <Button type="button" variant={isSelected ? "default" : "outline"} size="sm" aria-pressed={isSelected} aria-label={`${isSelected ? "Desmarcar" : "Selecionar"} ${recording.title}`} className={`h-10 w-full sm:h-9 sm:w-auto ${isSelected ? "bg-violet-600 hover:bg-violet-700" : ""}`} onClick={() => toggleRecording(recording.id)}>
                          {isSelected ? <Check className="w-3.5 h-3.5 mr-1" /> : <Square className="w-3.5 h-3.5 mr-1" />}
                          {isSelected ? "Selecionado" : "Selecionar"}
                        </Button>
                      )}
                      {canEdit && !selectable && (
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-violet-700" onClick={(event) => { event.stopPropagation(); setEditingRecording(recording); }} title="Editar título e descrição">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canDelete && !selectable && (
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-600" disabled={Boolean(deletingId)} onClick={(event) => { event.stopPropagation(); setRecordingToDelete(recording); }} title="Excluir procedimento">
                          {deletingId === recording.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      )}
                    </span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <AlertDialog open={Boolean(recordingToDelete)} onOpenChange={(open) => { if (!open && !deletingId) setRecordingToDelete(null); }}>
        <AlertDialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir procedimento?</AlertDialogTitle>
            <AlertDialogDescription className="[overflow-wrap:anywhere]">
              O procedimento <strong className="font-semibold text-gray-800">“{recordingToDelete?.title}”</strong> será excluído permanentemente da biblioteca. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:space-x-0">
            <AlertDialogCancel disabled={Boolean(deletingId)} className="mt-0">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(deletingId)}
              onClick={(event) => { event.preventDefault(); deleteRecording(recordingToDelete); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deletingId && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {deletingId ? "Excluindo..." : "Sim, excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ProcedureVideoPlayer recording={selectedRecording} onOpenChange={() => setSelectedRecording(null)} />
      <ProcedureRecordingEditDialog
        recording={editingRecording}
        onOpenChange={(open) => { if (!open) setEditingRecording(null); }}
        onSaved={async () => {
          setEditingRecording(null);
          await loadRecordings();
        }}
      />
    </>
  );
}
